import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DEFAULT_FILTERS_SEMAFOROS } from "../config/DEFAULT_FILTERS_SEMAFOROS";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useDebounce } from "../../../Hooks/UseDebounce";
import { UrlApi } from "../../../services/apirest";

const FiltersModal = ({
  open,
  onClose,
  filtros,
  setFiltros,
  sucursales,
  categorias,
  clientes,
}) => {
  const [localFilters, setLocalFilters] = useState(DEFAULT_FILTERS_SEMAFOROS);

  const [clientesEnvasado, setClientesEnvasado] = useState([]);
  const [busquedaFiltroCliente, setBusquedaFiltroCliente] = useState(null);
  const clienteDebounced = useDebounce(busquedaFiltroCliente, 500);

  console.log("Clientes: ", clientes);
  console.log("Sucursales: ", sucursales);
  console.log("Categorias: ", categorias);
  console.log("Envasado : ", localFilters.envasado);

  useEffect(() => {
    let vigente = true;

    const fetchClientes = async () => {
      try {
        const response = await fetch(
          `${UrlApi}/envasado_clientes/search?q=${clienteDebounced ?? ""}`,
        );

        if (!response.ok) {
          throw new Error("Error al obtener clientes");
        }

        const data = await response.json();

        if (vigente) {
          setClientesEnvasado(data);
        }
      } catch (err) {
        if (vigente) {
          console.error(err);
        }
      }
    };

    fetchClientes();

    return () => {
      vigente = false;
    };
  }, [clienteDebounced]);

  useEffect(() => {
    setLocalFilters(filtros);
  }, [filtros, open]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = () => {
    setFiltros(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters(DEFAULT_FILTERS_SEMAFOROS);
    setFiltros(DEFAULT_FILTERS_SEMAFOROS);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "info.main",
          color: "white",
          py: 2.5,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <FilterAltIcon sx={{ fontSize: 32 }} />

          <Box>
            <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
              Filtros del Semáforo
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Filtrá clientes por categoría, estado, fechas y sucursal.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: "#fafafa",
          py: 3,
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {localFilters.envasado === 0 && (
              //Cliente
              <Grid item xs={12}>
                <Autocomplete
                  options={clientes}
                  value={
                    clientes.find((c) => c.id === localFilters.idCliente) ??
                    null
                  }
                  getOptionLabel={(option) =>
                    `${option.id} - ${option.razon_social}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  filterOptions={(options, state) =>
                    options.filter(
                      (c) =>
                        c.razon_social
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase()) ||
                        String(c.id).includes(state.inputValue),
                    )
                  }
                  onChange={(_, value) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      idCliente: value?.id ?? null,
                      razonSocial: value?.razon_social ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Buscar por ID o Razón Social"
                    />
                  )}
                />
              </Grid>
            )}
            {localFilters.envasado === 1 && (
              // Clientes envasados
              <Grid item xs={12}>
                <Autocomplete
                  options={clientesEnvasado}
                  value={
                    clientesEnvasado.find(
                      (c) => c.id === localFilters.idCliente,
                    ) ?? null
                  }
                  getOptionLabel={(option) =>
                    `${option.id} - ${option.razon_social}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  filterOptions={(options) => options}
                  onChange={(_, value) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      idCliente: value?.id ?? null,
                      razonSocial: value?.razon_social ?? "",
                    }))
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      setBusquedaFiltroCliente(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente envasado"
                      placeholder="Buscar por ID o Razón Social"
                    />
                  )}
                />
              </Grid>
            )}

            {/* Categoría */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={categorias}
                value={
                  categorias.find((c) => c.id === localFilters.idCategoria) ??
                  null
                }
                getOptionLabel={(option) => option.categoria}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    idCategoria: value?.id ?? null,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Categoría" />
                )}
              />
            </Grid>

            {/* Sucursal */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={sucursales}
                value={
                  sucursales.find((s) => s.id === localFilters.idSucursal) ??
                  null
                }
                getOptionLabel={(option) => ` ${option.id} - ${option.nombre}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    idSucursal: value?.id ?? null,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Sucursal" />
                )}
              />
            </Grid>

            {/* Estado / Orden */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={localFilters.semaforo ?? ""}
                onChange={handleChange("semaforo")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ROJO">🔴 Rojo</MenuItem>
                <MenuItem value="AMARILLO">🟡 Amarillo</MenuItem>
                <MenuItem value="BLANCO">⚪ Blanco</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={localFilters.envasado}
                onChange={handleChange("envasado")}
              >
                <MenuItem value={0}>Granel</MenuItem>
                <MenuItem value={1}>Envasado</MenuItem>
              </TextField>
            </Grid>

            {/* Fechas */}
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                fullWidth
                label="Fecha desde"
                InputLabelProps={{ shrink: true }}
                value={localFilters.fechaDesde ?? ""}
                onChange={handleChange("fechaDesde")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                fullWidth
                label="Fecha hasta"
                InputLabelProps={{ shrink: true }}
                value={localFilters.fechaHasta ?? ""}
                onChange={handleChange("fechaHasta")}
              />
            </Grid>

            {/* Switch */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  background: "#fafafa",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(localFilters.soloConDeuda)}
                      onChange={handleChange("soloConDeuda")}
                    />
                  }
                  label="Mostrar solamente clientes con deuda"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          background: "#fafafa",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button variant="outlined" onClick={handleClear}>
          Limpiar
        </Button>

        <Box flex={1} />

        <Button color="inherit" onClick={onClose}>
          Cancelar
        </Button>

        <Button variant="contained" size="large" onClick={handleApply}>
          Aplicar filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
